import { useState } from "react";
import "../landing.css";

const WA = "https://wa.me/5524993185744";
const wa = (msg) => `${WA}?text=${encodeURIComponent(msg)}`;

const ExtLink = ({ href, className, children, testId }) => (
  <a className={className} href={href} target="_blank" rel="noopener noreferrer" data-testid={testId}>
    {children}
  </a>
);

const Eyebrow = ({ dark, children }) => (
  <p className={`eyebrow${dark ? " dark" : ""}`}><span></span> {children}</p>
);

const Brand = ({ className = "", testId }) => (
  <a className={`brand ${className}`} href="#inicio" aria-label="Ébano & Marfim, início" data-testid={testId}>
    <span className="brand-mark" aria-hidden="true"><i></i><i></i></span>
    <span className="brand-copy"><strong>Ébano</strong><em>&amp;</em><strong>Marfim</strong></span>
  </a>
);

const NAV = [
  ["#solucoes", "Soluções"],
  ["#atuacao", "Atuação"],
  ["#processo", "Como atuamos"],
  ["#parceiros", "Parceiros"],
];

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="site-header" id="inicio">
      <Brand testId="header-brand" />
      <button
        className={`menu-toggle${open ? " is-open" : ""}`}
        type="button"
        aria-expanded={open}
        aria-controls="main-nav"
        onClick={() => setOpen(!open)}
        data-testid="menu-toggle"
      >
        <span></span><span></span>
        <span className="sr-only">Abrir menu</span>
      </button>
      <nav className={`main-nav${open ? " is-open" : ""}`} id="main-nav" aria-label="Principal" data-testid="main-nav">
        {NAV.map(([href, label]) => (
          <a key={href} href={href} onClick={() => setOpen(false)} data-testid={`nav-${href.slice(1)}`}>{label}</a>
        ))}
      </nav>
      <ExtLink className="header-contact" href={wa("Olá, quero falar sobre as soluções da Ébano & Marfim.")} testId="header-whatsapp-btn">
        Falar com a Ébano <span aria-hidden="true">↗</span>
      </ExtLink>
    </header>
  );
}

function Hero() {
  return (
    <section className="hero section-dark" aria-labelledby="hero-title" data-testid="hero-section">
      <div className="hero-glow" aria-hidden="true"></div>
      <div className="hero-copy">
        <Eyebrow>Montagem e pós-venda B2B</Eyebrow>
        <h1 id="hero-title">A operação continua <em>depois</em> da entrega.</h1>
        <p className="hero-intro">
          Montagem de móveis e assistência técnica que dão previsibilidade para varejistas,
          fabricantes, redes parceiras e seguradoras.
        </p>
        <div className="hero-actions">
          <ExtLink className="button button-gold" href={wa("Olá, quero conhecer a operação B2B da Ébano & Marfim.")} testId="hero-whatsapp-btn">
            Conhecer nossas soluções <span aria-hidden="true">↗</span>
          </ExtLink>
          <a className="text-link light" href="#solucoes" data-testid="hero-scroll-link">Veja como atuamos <span aria-hidden="true">↓</span></a>
        </div>
      </div>
      <div className="hero-visual" aria-hidden="true">
        <img src="/hero-operacao-ebano.webp" alt="" />
        <div className="hero-photo-overlay"></div>
        <div className="hero-stamp">
          <span>É&amp;M</span>
          <p>Operação acompanhada<br />do início ao pós-venda.</p>
        </div>
      </div>
      <div className="hero-scroll"><span></span> role para explorar</div>
    </section>
  );
}

const SIGNALS = ["Montagem B2B", "Assistência técnica", "Pós-venda organizado", "Atendimento nacional"];

const SignalStrip = () => (
  <section className="signal-strip" aria-label="Áreas de atuação">
    {SIGNALS.map((s, i) => (
      <span key={s} style={{ display: "contents" }}>
        {i > 0 && <i></i>}<p>{s}</p>
      </span>
    ))}
  </section>
);

const Intro = () => (
  <section className="intro-section content-shell" id="solucoes" aria-labelledby="solucoes-title">
    <div className="section-heading heading-split">
      <Eyebrow dark>O que fazemos</Eyebrow>
      <h2 id="solucoes-title">Quando a venda termina, <em>a experiência começa.</em></h2>
    </div>
    <div className="intro-statement">
      <p>
        A Ébano &amp; Marfim organiza a etapa que mais influencia a percepção do cliente sobre a sua marca:
        a execução em campo, o cuidado técnico e a resposta após a entrega.
      </p>
      <ExtLink className="text-link dark" href={wa("Olá, quero entender como a Ébano & Marfim pode apoiar minha operação.")} testId="intro-whatsapp-link">
        Falar sobre minha operação <span aria-hidden="true">↗</span>
      </ExtLink>
    </div>
  </section>
);

const AssemblyCard = () => (
  <article className="solution-card assembly-card" data-testid="solution-assembly-card">
    <div className="solution-card-top">
      <span className="number">01</span>
      <span className="card-tag">Para empresas</span>
    </div>
    <div className="solution-visual assembly-visual" aria-hidden="true">
      <span className="blueprint-line one"></span><span className="blueprint-line two"></span><span className="blueprint-line three"></span>
      <div className="assembly-object"><span></span><span></span><span></span></div>
    </div>
    <div className="solution-content">
      <h3>Montagem de móveis<br /><em>para operações B2B.</em></h3>
      <p>
        Execução profissional para varejistas, fabricantes, lojas e redes parceiras. Cada atendimento é conduzido
        com padrão, organização e comunicação clara.
      </p>
      <ul>
        <li>Montagem para clientes empresariais</li>
        <li>Agendamento e coordenação de equipes</li>
        <li>Conferência e evidências de execução</li>
      </ul>
    </div>
  </article>
);

const COVERAGE = [["furniture", "Móveis"], ["mattress", "Colchões"], ["upholstery", "Estofados"], ["chair", "Cadeiras"]];

const AssistanceCard = () => (
  <article className="solution-card assistance-card" data-testid="solution-assistance-card">
    <div className="solution-card-top">
      <span className="number">02</span>
      <span className="card-tag">Para seguradoras</span>
    </div>
    <div className="solution-content assistance-content">
      <h3>Assistência técnica<br /><em>com responsabilidade.</em></h3>
      <p>
        Atendimento técnico organizado para sinistros, garantias e pós-venda, com foco na resolução correta e na
        experiência do segurado.
      </p>
    </div>
    <div className="coverage-grid" aria-label="Itens atendidos">
      {COVERAGE.map(([cls, label]) => (
        <div key={cls}><span className={`coverage-icon ${cls}`} aria-hidden="true"></span><p>{label}</p></div>
      ))}
    </div>
    <ExtLink className="card-action" href={wa("Olá, quero falar sobre assistência técnica para seguradoras.")} testId="assistance-whatsapp-btn">
      Falar sobre assistência técnica <span aria-hidden="true">↗</span>
    </ExtLink>
  </article>
);

const Solutions = () => (
  <section className="solutions content-shell" aria-label="Soluções da Ébano & Marfim">
    <AssemblyCard />
    <AssistanceCard />
  </section>
);

const AUDIENCE = [
  ["Varejo", "Uma entrega que continua bem representada na casa do cliente."],
  ["Indústria", "Execução alinhada ao padrão e à experiência da sua marca."],
  ["Seguradoras", "Assistência organizada para uma jornada mais segura do sinistro à solução."],
  ["Redes parceiras", "Uma extensão operacional confiável para atender melhor."],
];

const idx = (i) => String(i + 1).padStart(2, "0");

const Operation = () => (
  <section className="operation section-dark" id="atuacao" aria-labelledby="atuacao-title">
    <div className="content-shell operation-grid">
      <div className="operation-heading">
        <Eyebrow>Uma operação, múltiplas frentes</Eyebrow>
        <h2 id="atuacao-title">Do mobiliário à <em>tranquilidade</em> de quem recebe.</h2>
      </div>
      <div className="operation-copy">
        <p>
          Entramos onde uma operação precisa de mais controle: na montagem, na assistência, no retorno ao cliente e
          na visibilidade para quem acompanha o atendimento.
        </p>
      </div>
    </div>
    <div className="audience-rail content-shell">
      {AUDIENCE.map(([t, d], i) => (
        <article key={t}><span>{idx(i)}</span><h3>{t}</h3><p>{d}</p></article>
      ))}
    </div>
  </section>
);

const STEPS = [
  ["Entendemos a demanda", "Alinhamos o tipo de serviço, o contexto e os critérios da operação."],
  ["Coordenamos a execução", "Organizamos o atendimento e direcionamos o profissional adequado."],
  ["Acompanhamos em campo", "O serviço acontece com atenção ao ambiente, ao produto e ao cliente final."],
  ["Concluímos com responsabilidade", "Registramos a execução e damos continuidade quando houver necessidade de assistência."],
];

const Process = () => (
  <section className="process content-shell" id="processo" aria-labelledby="processo-title">
    <div className="process-header">
      <Eyebrow dark>Clareza em cada etapa</Eyebrow>
      <h2 id="processo-title">O cuidado está no processo.</h2>
      <p>Do primeiro contato à conclusão, cada demanda tem contexto, responsável e próximo passo.</p>
    </div>
    <ol className="process-list">
      {STEPS.map(([t, d], i) => (
        <li key={t}>
          <span className="process-index">{idx(i)}</span>
          <div><h3>{t}</h3><p>{d}</p></div>
        </li>
      ))}
    </ol>
  </section>
);

const DIFFS = [
  ["Qualidade que se vê", "Execução cuidadosa, padrão técnico e atenção a cada detalhe do ambiente."],
  ["Agilidade que responde", "Uma operação preparada para dar andamento e manter todos informados."],
  ["Organização que protege", "Etapas claras para reduzir retrabalho e preservar a experiência da marca."],
  ["Presença que resolve", "Atendimento humano com responsabilidade do primeiro chamado à assistência."],
];

const Difference = () => (
  <section className="difference" aria-labelledby="diferenciais-title">
    <div className="content-shell">
      <div className="difference-header">
        <Eyebrow dark>Por que Ébano &amp; Marfim</Eyebrow>
        <h2 id="diferenciais-title">Menos ruído. Mais <em>confiança</em> na ponta.</h2>
      </div>
      <div className="difference-list">
        {DIFFS.map(([t, d], i) => (
          <article key={t}><span>{idx(i)}</span><h3>{t}</h3><p>{d}</p></article>
        ))}
      </div>
    </div>
  </section>
);

const PARTNERS = [["Zurich", "Seguros"], ["Assurant", "Brasil"], ["Porto Seguro", "Seguros"], ["Lojas Competição", "RJ"], ["Gazin", "Lojas"]];

const Partners = () => (
  <section className="partners content-shell" id="parceiros" aria-labelledby="parceiros-title" data-testid="partners-section">
    <div className="partners-intro">
      <Eyebrow dark>Relações construídas em operação</Eyebrow>
      <h2 id="parceiros-title">Confiança é uma entrega repetida todos os dias.</h2>
      <p>Empresas que compartilham o compromisso com atendimento, qualidade e experiência do cliente.</p>
    </div>
    <div className="partner-names" aria-label="Parceiros">
      {PARTNERS.map(([n, s]) => (
        <div key={n} data-testid={`partner-${n.toLowerCase().replace(/\s+/g, "-")}`}><strong>{n}</strong><span>{s}</span></div>
      ))}
    </div>
    <p className="partner-note">Parceiros e marcas apresentados conforme informações públicas atuais da Ébano &amp; Marfim.</p>
  </section>
);

const Closing = () => (
  <section className="closing section-dark" id="contato" aria-labelledby="contato-title">
    <div className="closing-lines" aria-hidden="true"></div>
    <div className="content-shell closing-inner">
      <Eyebrow>Vamos conversar</Eyebrow>
      <h2 id="contato-title">Sua operação merece um pós-venda à altura da sua marca.</h2>
      <p>Fale com a nossa equipe sobre montagem de móveis B2B ou assistência técnica para seguradoras.</p>
      <ExtLink className="button button-gold large" href={wa("Olá, quero falar com a Ébano & Marfim sobre montagem ou assistência técnica.")} testId="closing-whatsapp-btn">
        Conversar no WhatsApp <span aria-hidden="true">↗</span>
      </ExtLink>
    </div>
  </section>
);

const Footer = () => (
  <footer className="site-footer section-dark">
    <div className="content-shell footer-grid">
      <Brand className="footer-brand" testId="footer-brand" />
      <p>Montagem de móveis B2B e assistência técnica com presença, padrão e responsabilidade.</p>
      <ExtLink className="footer-phone" href={wa("Olá, quero falar com a Ébano & Marfim.")} testId="footer-whatsapp-link">
        <span>WhatsApp comercial</span><strong>+55 24 99318-5744</strong>
      </ExtLink>
    </div>
    <div className="content-shell footer-bottom">
      <span data-testid="footer-year">© {new Date().getFullYear()} Ébano &amp; Marfim</span>
      <span>Montagem · Assistência técnica · Pós-venda</span>
    </div>
  </footer>
);

export default function LandingB2B() {
  return (
    <div className="landing-b2b">
      <a className="skip-link" href="#conteudo">Ir para o conteúdo</a>
      <Header />
      <main id="conteudo">
        <Hero />
        <SignalStrip />
        <Intro />
        <Solutions />
        <Operation />
        <Process />
        <Difference />
        <Partners />
        <Closing />
      </main>
      <Footer />
    </div>
  );
}
